/** @format */
"use client";

import React, { useEffect, useState } from "react";
import { motion, Variants } from "framer-motion";
import {
  Youtube,
  Music2,
  MapPin,
  ExternalLink,
  Guitar,
  Facebook,
  Coffee,
  Heart,
  Users,
  Wrench,
  Layers,
  GiftIcon,
  MonitorPlayIcon,
} from "lucide-react";
import { PageViewAPI } from "@/api/pageView/page-view.api";

/* ─────────────────────────────────────────
   DATA
───────────────────────────────────────── */

const socialLinks = [
  {
    name: "Facebook",
    icon: Facebook,
    url: "https://www.facebook.com/Midomotoj/",
    label: "Theo dõi",
  },
  {
    name: "TikTok",
    icon: Music2,
    url: "https://www.tiktok.com/@cuongchiguitar",
    label: "Xem video",
  },
  {
    name: "YouTube",
    icon: Youtube,
    url: "https://www.youtube.com/@Cuong-Guitar-1997",
    label: "Subscribe",
  },
];

const customLinks = [
  {
    title: "Khóa Học Guitar Hình Thức Video (Mới Ra Mắt)",
    description: "Các bạn cứ vào đường link rồi đăng ký tài khoản ",
    url: "https://learn.guitarsaigon.com",
    icon: MonitorPlayIcon,
  },
  // {
  //   title: "Link Đăng ký học Guitar",
  //   description: "Lớp Guitar Cơ Bản và Nâng Cao",
  //   url: "https://guitarsaigon.com/dang-ky-hoc-guitar",
  //   icon: Guitar,
  // },
  {
    title: "Group Facebook Guitar",
    description: "Cộng đồng học guitar – chia sẻ kiến thức, hỏi đáp mỗi ngày",
    url: "https://www.facebook.com/groups/812999921786147",
    icon: Users,
  },
  {
    title: "Hợp Âm Guitar Online",
    description: "Công cụ tra cứu hợp âm bất kỳ – miễn phí, không cần cài app",
    url: "https://guitarsaigon.com/hop-am-guitar",
    icon: Guitar,
  },
  {
    title: "Metronome Chuyên Nghiệp",
    description: "Đầy đủ tính năng như app tính phí – hoàn toàn miễn phí",
    url: "https://guitarsaigon.com/metronome",
    icon: Wrench,
  },
];

/* ─────────────────────────────────────────
   ANIMATION VARIANTS
───────────────────────────────────────── */

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } },
};

const itemVariants: Variants = {
  hidden: { y: 24, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: { type: "spring", stiffness: 90, damping: 18 },
  },
};

/* ─────────────────────────────────────────
   COMPONENT
───────────────────────────────────────── */

const AboutUsPage: React.FC = () => {
  const [hoveredLink, setHoveredLink] = useState<number | null>(null);
  const [showQR, setShowQR] = useState(false);

  useEffect(() => {
    PageViewAPI.createUserView();
  }, []);

  return (
    <div
      className="min-h-screen py-12 px-4"
      style={{
        backgroundColor: "var(--background)",
        fontFamily: "var(--font-roboto)",
      }}>
      {/* QR Fullscreen overlay */}
      {showQR && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center p-6"
          style={{
            backgroundColor: "rgba(0,0,0,0.85)",
            backdropFilter: "blur(8px)",
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          onClick={() => setShowQR(false)}>
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: "spring", stiffness: 200 }}
            className="rounded-3xl p-5 max-w-sm w-full"
            style={{
              backgroundColor: "var(--card)",
              border: "1px solid var(--border)",
            }}
            onClick={(e) => e.stopPropagation()}>
            <p
              className="text-center text-sm font-bold uppercase tracking-widest mb-4"
              style={{
                color: "var(--brand-primary)",
                fontFamily: "var(--font-montserrat)",
              }}>
              Quét để ủng hộ
            </p>
            <img
              src="./my-qr.jpg"
              alt="QR Code"
              className="w-full rounded-2xl"
            />
            <button
              onClick={() => setShowQR(false)}
              className="mt-4 w-full py-3 rounded-xl text-sm font-semibold transition-opacity hover:opacity-80"
              style={{
                backgroundColor: "var(--border)",
                color: "var(--muted-foreground)",
                fontFamily: "var(--font-montserrat)",
              }}>
              Đóng
            </button>
          </motion.div>
        </motion.div>
      )}

      <motion.div
        className="max-w-xl mx-auto"
        variants={containerVariants}
        initial="hidden"
        animate="visible">
        {/* ── PROFILE ── */}
        <motion.div variants={itemVariants} className="text-center mb-12">
          {/* Avatar */}
          <motion.div
            className="relative w-28 h-28 mx-auto mb-6"
            whileHover={{ scale: 1.04 }}
            transition={{ type: "spring", stiffness: 300 }}>
            {/* Gold ring */}
            <div
              className="absolute inset-0 rounded-full"
              style={{
                background:
                  "conic-gradient(from 0deg, var(--brand-primary), transparent 60%, var(--brand-primary))",
                padding: "2px",
              }}
            />
            <div
              className="absolute inset-[3px] rounded-full overflow-hidden"
              style={{ backgroundColor: "var(--background)" }}>
              <img
                src="/cuong.jpg"
                alt="Cường Guitar"
                className="w-full h-full object-cover rounded-full"
              />
            </div>

            {/* Online dot */}
            <div
              className="absolute bottom-1 right-1 w-4 h-4 rounded-full border-2"
              style={{
                backgroundColor: "#22c55e",
                borderColor: "var(--background)",
              }}
            />
          </motion.div>

          {/* Name */}
          <motion.h1
            variants={itemVariants}
            className="text-4xl sm:text-5xl font-black mb-2 tracking-tight"
            style={{
              fontFamily: "var(--font-montserrat)",
              color: "var(--foreground)",
            }}>
            Cường <span style={{ color: "var(--brand-primary)" }}>Guitar</span>
          </motion.h1>

          {/* Role tags */}
          <motion.div
            variants={itemVariants}
            className="flex items-center justify-center flex-wrap gap-2 mb-4">
            {["Content Creator", "Guitar Teacher", "IT & Design"].map((tag) => (
              <span
                key={tag}
                className="px-3 py-1 rounded-full text-xs font-semibold border"
                style={{
                  fontFamily: "var(--font-montserrat)",
                  backgroundColor: "rgba(200,169,110,0.08)",
                  borderColor: "rgba(200,169,110,0.3)",
                  color: "var(--brand-primary)",
                }}>
                {tag}
              </span>
            ))}
          </motion.div>

          {/* Location */}
          <motion.p
            variants={itemVariants}
            className="flex items-center justify-center gap-1.5 text-sm mb-3"
            style={{ color: "var(--muted-foreground)" }}>
            <MapPin
              className="w-3.5 h-3.5"
              style={{ color: "var(--brand-primary)" }}
            />
            Quận 12, TP. Hồ Chí Minh
          </motion.p>

          {/* Bio */}
          <motion.p
            variants={itemVariants}
            className="text-sm leading-relaxed max-w-sm mx-auto"
            style={{ color: "var(--muted-foreground)" }}>
            Chia sẻ hành trình guitar — từ hợp âm đầu tiên đến những giai điệu
            bạn yêu thích ✨
          </motion.p>
        </motion.div>

        {/* ── SOCIAL LINKS ── */}
        <motion.div
          variants={itemVariants}
          className="grid grid-cols-3 gap-3 mb-6">
          {socialLinks.map(({ name, icon: Icon, url, label }) => (
            <motion.a
              key={name}
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ y: -4, scale: 1.03 }}
              whileTap={{ scale: 0.96 }}
              transition={{ type: "spring", stiffness: 400, damping: 18 }}
              className="flex flex-col items-center gap-2 py-5 px-3 rounded-2xl border transition-colors duration-200"
              style={{
                backgroundColor: "var(--card)",
                borderColor: "var(--border)",
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLElement).style.borderColor =
                  "rgba(200,169,110,0.5)";
                (e.currentTarget as HTMLElement).style.boxShadow =
                  "0 4px 20px rgba(200,169,110,0.12)";
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLElement).style.borderColor =
                  "var(--border)";
                (e.currentTarget as HTMLElement).style.boxShadow = "none";
              }}>
              <div
                className="w-11 h-11 rounded-xl flex items-center justify-center"
                style={{
                  backgroundColor: "rgba(200,169,110,0.1)",
                  border: "1.5px solid rgba(200,169,110,0.25)",
                }}>
                <Icon
                  className="w-5 h-5"
                  style={{ color: "var(--brand-primary)" }}
                />
              </div>
              <p
                className="font-bold text-sm"
                style={{
                  fontFamily: "var(--font-montserrat)",
                  color: "var(--foreground)",
                }}>
                {name}
              </p>
              <p
                className="text-xs"
                style={{ color: "var(--muted-foreground)" }}>
                {label}
              </p>
            </motion.a>
          ))}
        </motion.div>

        {/* ── CUSTOM LINKS ── */}
        <motion.div variants={itemVariants} className="space-y-3 mb-6">
          <p
            className="text-xs font-bold uppercase tracking-widest mb-4 px-1"
            style={{
              color: "var(--muted-foreground)",
              fontFamily: "var(--font-montserrat)",
            }}>
            Công cụ & Cộng đồng
          </p>

          {customLinks.map(({ title, description, url, icon: Icon }, index) => (
            <motion.a
              key={index}
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              onHoverStart={() => setHoveredLink(index)}
              onHoverEnd={() => setHoveredLink(null)}
              whileHover={{ x: 4 }}
              whileTap={{ scale: 0.98 }}
              transition={{ type: "spring", stiffness: 400, damping: 20 }}
              className="flex items-center gap-4 p-5 rounded-2xl border transition-all duration-200 w-full"
              style={{
                backgroundColor: "var(--card)",
                borderColor:
                  hoveredLink === index
                    ? "rgba(200,169,110,0.45)"
                    : "var(--border)",
                boxShadow:
                  hoveredLink === index
                    ? "0 4px 20px rgba(200,169,110,0.1)"
                    : "none",
              }}>
              {/* Icon */}
              <div
                className="shrink-0 w-11 h-11 rounded-xl flex items-center justify-center"
                style={{
                  backgroundColor: "rgba(200,169,110,0.1)",
                  border: "1.5px solid rgba(200,169,110,0.25)",
                }}>
                <Icon
                  className="w-5 h-5"
                  style={{ color: "var(--brand-primary)" }}
                />
              </div>

              {/* Text */}
              <div className="flex-1 min-w-0">
                <p
                  className="font-bold text-sm mb-0.5 truncate"
                  style={{
                    fontFamily: "var(--font-montserrat)",
                    color: "var(--foreground)",
                  }}>
                  {title}
                </p>
                <p
                  className="text-xs leading-relaxed"
                  style={{ color: "var(--muted-foreground)" }}>
                  {description}
                </p>
              </div>

              {/* Arrow */}
              <motion.div
                animate={{ x: hoveredLink === index ? 4 : 0 }}
                transition={{ type: "spring", stiffness: 400 }}
                className="shrink-0">
                <ExternalLink
                  className="w-4 h-4"
                  style={{ color: "var(--brand-primary)" }}
                />
              </motion.div>
            </motion.a>
          ))}
        </motion.div>

        {/* ── SUPPORT / QR ── */}
        <motion.div variants={itemVariants} className="mb-10">
          <div
            className="rounded-3xl p-7 border text-center"
            style={{
              backgroundColor: "var(--card)",
              borderColor: "rgba(200,169,110,0.3)",
              boxShadow: "0 0 40px rgba(200,169,110,0.06)",
            }}>
            {/* Gold top bar */}
            <div
              className="h-1 w-10 rounded-full mx-auto mb-6"
              style={{ backgroundColor: "var(--brand-primary)" }}
            />

            <div className="flex items-center justify-center gap-2 mb-2">
              <Heart
                className="w-5 h-5"
                style={{ color: "#f43f5e" }}
                fill="#f43f5e"
              />
              <h3
                className="text-xl font-black"
                style={{
                  fontFamily: "var(--font-montserrat)",
                  color: "var(--foreground)",
                }}>
                Ủng hộ mình tại đây
              </h3>
              <Coffee className="w-5 h-5" style={{ color: "#f59e0b" }} />
            </div>

            <p
              className="text-sm leading-relaxed mb-6 max-w-xs mx-auto"
              style={{ color: "var(--muted-foreground)" }}>
              Nếu bạn thấy nội dung hữu ích, hãy ủng hộ để mình có thêm động lực
              tạo ra nhiều hơn 💖
            </p>

            {/* QR */}
            <motion.div
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              className="inline-block cursor-pointer"
              onClick={() => setShowQR(true)}>
              <div
                className="rounded-2xl p-4 inline-block"
                style={{
                  backgroundColor: "var(--background)",
                  border: "1.5px solid rgba(200,169,110,0.3)",
                  boxShadow: "0 4px 20px rgba(200,169,110,0.12)",
                }}>
                <img
                  src="./my-qr.jpg"
                  alt="QR Code ủng hộ"
                  className="w-44 h-44 object-cover rounded-xl"
                />
              </div>
              <p
                className="text-xs mt-3"
                style={{ color: "var(--muted-foreground)" }}>
                Nhấn để phóng to • Quét để chuyển khoản
              </p>
            </motion.div>
          </div>
        </motion.div>

        {/* ── FOOTER ── */}
        <motion.div variants={itemVariants} className="text-center">
          <div
            className="h-px mb-6 mx-auto w-24 rounded-full"
            style={{ backgroundColor: "var(--border)" }}
          />
          <p className="text-xs" style={{ color: "var(--muted-foreground)" }}>
            © 2025{" "}
            <span
              style={{
                color: "var(--brand-primary)",
                fontFamily: "var(--font-montserrat)",
                fontWeight: 700,
              }}>
              Nguyễn Cường
            </span>{" "}
            · All rights reserved.
          </p>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default AboutUsPage;
